import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocketMessage, useSocketState } from '../socket/useSocket';
import { queryKeys } from '@/lib/query-keys';
import { getSocketManager } from '@/lib/socketManager';
import type { QueuedMessage, WsServerMessage } from '@/lib/types';

/**
 * Subscribes the React Query cache for `queryKeys.agentQueue(agentId)` to
 * the server's `agent:queue_updated` WebSocket broadcasts. Every mutation
 * the server makes (enqueue, update, remove, reorder, drain) reaches the
 * client through this single message — no diffs, just the new full list.
 *
 * The queue cache is broadcast-only (`useMessageQueue` reads it with
 * `staleTime: Infinity`), so a drain or clear that lands while the socket is
 * down (a deploy, a network blip, a backgrounded tab) would otherwise leave a
 * consumed message on screen forever — and deleting that stale row 404s. To
 * self-heal, re-fetch the authoritative queue whenever the socket transitions
 * back to open or the tab returns to the foreground after a hidden stretch.
 */
export function useAgentQueueSocket(agentId: string | null): void {
  const queryClient = useQueryClient();

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type !== 'agent:queue_updated') return;
        if (!agentId || msg.agentId !== agentId) return;
        queryClient.setQueryData<QueuedMessage[]>([...queryKeys.agentQueue(agentId)], msg.queue);
      },
      [agentId, queryClient],
    ),
  );

  const socketState = useSocketState();
  const wasOpenRef = useRef(socketState === 'open');
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = socketState === 'open';
    // Only heal on a genuine re-open (closed/connecting -> open); every mount
    // already re-fetches the queue through `useMessageQueue`.
    if (!agentId || socketState !== 'open' || wasOpen) return;
    queryClient.invalidateQueries({ queryKey: [...queryKeys.agentQueue(agentId)] });
  }, [socketState, agentId, queryClient]);

  // The socket state watcher above only fires on a closed→open transition.
  // When the tab is backgrounded and the socket stays alive, the queue can be
  // drained server-side with no broadcast reaching the throttled page — the
  // stream resyncs via the socket manager's resync callbacks, but the queue
  // does not. Register the same kind of callback so the queue is refetched
  // on tab refocus and reconnect, matching the stream's resync path.
  useEffect(() => {
    if (!agentId) return;
    const manager = getSocketManager();
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.agentQueue(agentId)] });
    };
    manager.addReconnectCallback(handler);
    return () => manager.removeReconnectCallback(handler);
  }, [agentId, queryClient]);
}
